import {
  amountToMinorUnits,
  formatMoneyFromMinorUnits,
  normaliseCurrencyCode,
  stripeCurrencyExponent
} from "./currencyUtils.js";
import {
  subscriptionCountryPricingData,
  subscriptionCountryPricingSources,
  subscriptionCountryPricingVersion
} from "./subscriptionCountryPricingData.js";

export const subscriptionIncomeAdjustments = Object.freeze({
  HIC: 1,
  UMC: 0.82,
  LMC: 0.65,
  LIC: 0.5,
  NA: 0.8
});

const subscriptionUsdPriceBounds = Object.freeze({
  HIC: { floor: 0.5, ceiling: 1.35 },
  UMC: { floor: 0.3, ceiling: 0.9 },
  LMC: { floor: 0.18, ceiling: 0.65 },
  LIC: { floor: 0.12, ceiling: 0.5 },
  NA: { floor: 0.22, ceiling: 0.8 }
});

const cleanCountryCode = (value = "", fallback = "AU") => {
  const countryCode = String(value || "").trim().toUpperCase();
  if (subscriptionCountryPricingData[countryCode]) return countryCode;
  return subscriptionCountryPricingData[fallback] ? fallback : "AU";
};

const friendlyPriceAmount = (value, currency = "aud") => {
  const amount = Math.max(0, Number(value) || 0);
  if (stripeCurrencyExponent(currency) === 0) {
    const step = amount < 100 ? 1 : amount < 1_000 ? 10 : amount < 10_000 ? 50 : amount < 100_000 ? 500 : 1_000;
    return Math.max(1, Math.round(amount / step) * step);
  }
  if (amount < 1) return 0.99;
  if (amount < 10) return Math.max(0.99, Math.floor(amount) + 0.99);
  if (amount < 50) return Math.max(0.99, Math.round(amount) - 0.01);
  if (amount < 100) return Math.max(0.99, Math.round(amount / 5) * 5 - 0.01);
  if (amount < 1_000) return Math.max(1, Math.round(amount / 10) * 10 - 1);
  if (amount < 10_000) return Math.max(1, Math.round(amount / 100) * 100 - 1);
  if (amount < 100_000) return Math.max(1, Math.round(amount / 1_000) * 1_000 - 1);
  return Math.max(1, Math.round(amount / 10_000) * 10_000 - 1);
};

const hasCurrentFx = (country = {}) => Number(country.fx) > 0 && Number(country.fxYear) >= 2023;
const hasCurrentPpp = (country = {}) => Number(country.ppp) > 0 && Number(country.pppYear) >= 2022;
const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const usdPriceBounds = (baseAudCents, country = {}) => {
  const baseUsdAmount = (Math.max(0, Number(baseAudCents) || 0) / 100)
    / subscriptionCountryPricingSources.baseFx;
  const bounds = subscriptionUsdPriceBounds[country.incomeLevel] || subscriptionUsdPriceBounds.NA;
  return {
    minimum: baseUsdAmount * bounds.floor,
    maximum: baseUsdAmount * bounds.ceiling
  };
};

const localizedMajorAmount = ({ baseAudCents, country, currencyMode }) => {
  const baseAudAmount = Math.max(0, Number(baseAudCents) || 0) / 100;
  const incomeAdjustment = subscriptionIncomeAdjustments[country.incomeLevel] || subscriptionIncomeAdjustments.NA;
  const bounds = usdPriceBounds(baseAudCents, country);
  if (currencyMode === "native") {
    const nativeAmount = hasCurrentPpp(country)
      ? baseAudAmount * (country.ppp / subscriptionCountryPricingSources.basePpp) * incomeAdjustment
      : baseAudAmount * (country.fx / subscriptionCountryPricingSources.baseFx) * incomeAdjustment;
    if (hasCurrentFx(country)) {
      return clamp(nativeAmount, bounds.minimum * country.fx, bounds.maximum * country.fx);
    }
    return nativeAmount;
  }
  const usdAmount = hasCurrentPpp(country) && hasCurrentFx(country)
    ? baseAudAmount
      * (country.ppp / subscriptionCountryPricingSources.basePpp)
      * incomeAdjustment
      / country.fx
    : baseAudAmount / subscriptionCountryPricingSources.baseFx * incomeAdjustment;
  return clamp(usdAmount, bounds.minimum, bounds.maximum);
};

const priceCentsFor = ({ baseAudCents, countryCode, supportedPaymentCurrencies }) => {
  const country = subscriptionCountryPricingData[countryCode] || subscriptionCountryPricingData.AU;
  if (countryCode === "AU") return { cents: Math.max(0, Number(baseAudCents) || 0), currency: "aud", currencyMode: "native" };
  const nativeCurrency = normaliseCurrencyCode(country.currency, "usd");
  const supported = supportedPaymentCurrencies instanceof Set
    ? supportedPaymentCurrencies
    : Array.isArray(supportedPaymentCurrencies)
      ? new Set(supportedPaymentCurrencies.map((currency) => normaliseCurrencyCode(currency)))
      : null;
  const currencyMode = (!supported || supported.has(nativeCurrency)) && hasCurrentFx(country)
    ? "native"
    : "usd_fallback";
  const currency = currencyMode === "native" ? nativeCurrency : "usd";
  const majorAmount = localizedMajorAmount({ baseAudCents, country, currencyMode: currencyMode === "native" ? "native" : "usd" });
  return {
    cents: amountToMinorUnits(friendlyPriceAmount(majorAmount, currency), currency),
    currency,
    currencyMode
  };
};

export const getLocalizedProfileSubscriptionOffer = (
  plan,
  requestedCountryCode = "AU",
  { supportedPaymentCurrencies } = {}
) => {
  const countryCode = cleanCountryCode(requestedCountryCode);
  const country = subscriptionCountryPricingData[countryCode] || subscriptionCountryPricingData.AU;
  const monthly = priceCentsFor({
    baseAudCents: plan?.monthlyCents,
    countryCode,
    supportedPaymentCurrencies
  });
  const annual = priceCentsFor({
    baseAudCents: plan?.annualCents,
    countryCode,
    supportedPaymentCurrencies
  });
  const annualCents = Math.min(annual.cents, Math.max(1, monthly.cents * 12 - (stripeCurrencyExponent(monthly.currency) === 0 ? 1 : 100)));
  return {
    countryCode,
    countryName: country.name,
    incomeLevel: country.incomeLevel,
    currency: monthly.currency,
    monthlyCents: monthly.cents,
    annualCents,
    annualSavingCents: Math.max(0, monthly.cents * 12 - annualCents),
    currencyMode: monthly.currencyMode,
    pricingVersion: subscriptionCountryPricingVersion,
    pppYear: country.pppYear,
    fxYear: country.fxYear,
    methodology: subscriptionCountryPricingSources.methodology
  };
};

export const inferSubscriptionCountryCode = (fallback = "AU") => {
  if (typeof navigator === "undefined") return cleanCountryCode(fallback);
  for (const language of navigator.languages || [navigator.language]) {
    try {
      const region = new Intl.Locale(language).region;
      if (region && subscriptionCountryPricingData[region]) return region;
    } catch {
      const match = String(language || "").match(/[-_]([A-Za-z]{2})$/);
      if (match && subscriptionCountryPricingData[match[1].toUpperCase()]) return match[1].toUpperCase();
    }
  }
  return cleanCountryCode(fallback);
};

export const localizedSubscriptionPriceLabel = (offer, billingInterval = "monthly") => (
  formatMoneyFromMinorUnits(
    billingInterval === "annual" ? offer?.annualCents : offer?.monthlyCents,
    offer?.currency || "aud"
  )
);
